import { rows_to_pdf } from './company_reports_pdf_helpers';
import {
  structured_report_sheet_rows,
  structured_report_to_csv,
} from './company_reports_structured_export_helpers';
import { sheet_rows_to_xlsx } from './company_reports_xlsx_helpers';

const REPORT_EXPORT_FIELDS = new Set([
  'action', 'auto_decision', 'billable_hours', 'case_code', 'category', 'client_case_name',
  'date', 'date_range', 'description', 'domain', 'format', 'hourly_rate', 'matter_id',
  'metric', 'non_billable_hours', 'owner', 'practice_area', 'report', 'scope', 'score',
  'section', 'status', 'task', 'timekeeper', 'title', 'total_hours', 'total_value', 'user',
  'value', 'risk_level',
]);

const AUDIT_LOG_READERS = new Map([
  ['action', (log) => log.action],
  ['action_type', (log) => log.action_type],
  ['case_code', (log) => log.case_code],
  ['application_code', (log) => log.application_code],
  ['reference', (log) => log.reference],
  ['status', (log) => log.status],
  ['success', (log) => log.success],
  ['user_full_name', (log) => log.user_full_name],
  ['user_email', (log) => log.user_email],
  ['username', (log) => log.username],
  ['date_created', (log) => log.date_created],
  ['timestamp', (log) => log.timestamp],
  ['created_at', (log) => log.created_at],
  ['message', (log) => log.message],
]);

export function download_company_report(report = {}, options = {}) {
  const rows = report.rows?.length ? report.rows : [summary_row(report, options)];
  const file = report_file(report, rows, options);
  const link = document.createElement('a');

  link.href = URL.createObjectURL(file.blob);
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

export function risk_report_row(case_item = {}, result = {}) {
  return {
    case_code: case_item.case_code,
    domain: case_item.domain,
    score: result.total_score || result.risk_score || result.score || '',
    risk_level: result.risk_level || result.risk_band || '',
    auto_decision: result.auto_decision || '',
  };
}

export function case_report_row(case_item = {}) {
  return {
    case_code: case_item.case_code,
    title: case_item.title,
    domain: case_item.domain,
    status: case_item.status,
    owner: case_item.owner,
  };
}

export function audit_report_row(log = {}) {
  return {
    action: first_log_value(log, audit_action_fields(log)),
    status: first_log_value(log, ['status', 'success']),
    user: first_log_value(log, ['user_full_name', 'user_email', 'username']),
    date: first_log_value(log, ['date_created', 'timestamp', 'created_at']),
  };
}

function audit_action_fields(log = {}) {
  return audit_case_reference(log)
    ? ['action']
    : ['action', 'action_type', 'message'];
}

function audit_case_reference(log = {}) {
  return first_log_value(log, ['case_code', 'application_code', 'reference']);
}

function first_log_value(log = {}, fields = []) {
  return fields.map((field) => log_value(log, field)).find(is_present_value) ?? '';
}

function log_value(log = {}, field = '') {
  const read_value = AUDIT_LOG_READERS.get(field);
  return typeof read_value === 'function' ? read_value(log) : undefined;
}

function is_present_value(value) {
  return value !== undefined && value !== null && value !== '';
}

function summary_row(report = {}, options = {}) {
  return {
    report: report.title || 'Company report',
    category: report.category_label || '',
    date_range: options.date_range || '',
    scope: options.scope || '',
    format: options.format || '',
    description: report.description || '',
  };
}

function report_file(report = {}, rows = [], options = {}) {
  const filename = safe_filename(report.title || 'company-report');
  const structured = Boolean(report.sections?.length);
  const builders = new Map([
    ['csv', () => text_file(`${filename}.csv`, structured ? structured_report_to_csv(report, options) : rows_to_csv(rows), 'text/csv;charset=utf-8;')],
    ['xlsx', () => binary_file(`${filename}.xlsx`, structured ? sheet_rows_to_xlsx(structured_report_sheet_rows(report, options)) : rows_to_xlsx(rows), 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')],
    ['pdf', () => binary_file(`${filename}.pdf`, rows_to_pdf(report, rows, options), 'application/pdf')],
  ]);
  return (builders.get(options.format) || builders.get('csv'))();
}

function text_file(name, content, type) {
  return { name, blob: new Blob([content], { type }) };
}

function binary_file(name, content, type) {
  return { name, blob: new Blob([content], { type }) };
}

function rows_to_csv(rows = []) {
  const headers = report_headers(rows);
  return [
    headers.join(','),
    ...rows.map((row) => headers.map((header) => csv_value(safe_value(row, header, headers))).join(',')),
  ].join('\n');
}

function csv_value(value = '') {
  return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

function rows_to_xlsx(rows = []) {
  const headers = report_headers(rows);
  return sheet_rows_to_xlsx([
    { cells: headers, style: 'header' },
    ...rows.map((row) => ({ cells: headers.map((header) => safe_value(row, header, headers)) })),
  ]);
}

function report_headers(rows = []) {
  return [...new Set(rows.flatMap((row) => Object.keys(row)))].filter(is_allowed_report_field);
}

function safe_value(row = {}, key = '', allowed_keys = []) {
  if (!allowed_keys.includes(key) || !is_allowed_report_field(key)) return '';
  return safe_lookup(row, key);
}

function is_allowed_report_field(key = '') {
  return REPORT_EXPORT_FIELDS.has(key);
}

function safe_lookup(record = {}, key = '') {
  const entry = Object.entries(record).find(([entry_key]) => entry_key === key);
  return Array.isArray(entry) && entry.length > 1 ? entry[1] ?? '' : '';
}

function safe_filename(value = '') {
  return String(value || 'company-report')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
